import {
  CarouselImageSlider,
  DateRangePicker,
  StatsMatrix,
  StatsContainer,
  ChartContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  ChartTooltip,
  ChartTooltipContent,
  Skeleton,
  SearchBar,
  DataTable,
  NotificationCard,
} from "@/components";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useState } from "react";
import { FaBox, FaIndianRupeeSign } from "react-icons/fa6";
import { IoWallet } from "react-icons/io5";
import ColumnHelper from "@/utils/ColumnHelper";
import { RiFileExcel2Line } from "react-icons/ri";
import { SpaceWrapper } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { TfiStatsUp } from "react-icons/tfi";
import { fetchDashboardData, selectAllNotifications } from "../../app/slices";
import { subDays } from "date-fns";
import { useNavigate } from "react-router-dom";

const chartConfig = {
  Distribution: { label: "Distribution", color: "#9DD257" },
};

// Reusable Chart Section
const ChartSection = ({ chartData }) => (
  <div className="flex flex-col gap-y-2 w-full md:w-1/2 bg-background_2/90 rounded-xl p-3">
    <ChartContainer
      layout="vertical"
      config={chartConfig}
      className="h-[200px] w-full"
    >
      <BarChart accessibilityLayer data={chartData}>
        <CartesianGrid vertical={true} className={"opacity-40"} />
        <XAxis
          dataKey="Zone"
          tickLine={false}
          tickMargin={10}
          axisLine={false}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        <Bar dataKey="Shipments" fill="#9DD257" radius={4} />
      </BarChart>
    </ChartContainer>
  </div>
);

// Reusable Notification Skeleton
const NotificationSkeleton = () => (
  <div className="flex items-center space-x-4 bg-background rounded-xl p-3">
    <Skeleton className="h-12 w-12 rounded-xl bg-background_2/90" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-[150px] bg-background_2/90" />
      <Skeleton className="h-4 w-[100px] bg-background_2/90" />
    </div>
  </div>
);

const DashBoard = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const walletBalance = useSelector((state) => state.wallet.balance);
  const dashboardData = useSelector((state) => state.dashboard.data);
  console.log("ShipmentsData>>>>>>>>>", dashboardData?.shipments);
  const banners = useSelector((state) => state?.app?.appData?.banners) || [];
  const shipments = dashboardData?.shipments || {};

  // Delivered percentage denominator: only forward-delivery statuses.
  // RTO, Cancelled, and pendingPickup are intentionally excluded so they
  // do not inflate the base and skew the delivery rate downward.
  const effectiveTotal =
    (shipments.pending || 0) +
    (shipments.delivered || 0) +
    (shipments.inTransit || 0) +
    (shipments.outForDelivery || 0);

  const [notifications, setNotifications] = useState([]);

  const notificationsData = useSelector(selectAllNotifications) || [];

  const notificationLoading =
    useSelector((state) => state.notification?.loading) || false;

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  useEffect(() => {
    if (notificationsData.length > 0) {
      // Take only the 2 most recent notifications
      const recentNotifications = notificationsData.slice(0, 2);

      setNotifications(
        recentNotifications?.map((notification) => ({
          id: notification._id, // Use _id from API response
          image: notification.content?.imageUrl || "", // Ensure it doesn't break if imageUrl is missing
          title: notification.content?.title || "No Title",
          date: formatDate(notification.createdAt),
          description: notification.content?.message || "No Description",
        }))
      );
    }
  }, [notificationsData]);

  const DeliveryInfoData = [
    {
      type: "icon",
      title: "Total Revenue",
      subtitle: "Total value of delivered orders (in Rs.)",
      icon: TfiStatsUp,
      value: dashboardData?.shipments?.totalOrderAmount || 0,
    },
    {
      type: "icon",
      title: "Avg. Order Cost ",
      subtitle: "Your average shipping cost of all orders",
      icon: FaBox,
      value: (
        (dashboardData?.shipments?.totalFreightCharge +
          dashboardData?.shipments?.totalCodCharge) /
        dashboardData?.shipments?.totalShipments || 0
      ).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    },
    {
      type: "progress",
      title: "Delivered Percentage ",
      subtitle: "Total orders delivered (in percentage)",
      current: dashboardData?.shipments?.delivered || 0,
      total: effectiveTotal || 0,
      label: "orders delivered",
    },
  ];

  const StatsShipment = [
    {
      statusName: "Total Shipments",
      statusNumber: dashboardData?.shipments?.totalShipments || 0,
    },
    {
      statusName: "Pending",
      statusNumber: dashboardData?.shipments?.pending || 0,
    },
    {
      statusName: "In Transit",
      statusNumber: dashboardData?.shipments?.inTransit || 0,
    },
    {
      statusName: "Out for Delivery",
      statusNumber: dashboardData?.shipments?.outForDelivery || 0,
    },
    {
      statusName: "Delivered",
      statusNumber: dashboardData?.shipments?.delivered || 0,
    },
  ];

  const StatsNdr = [
    {
      statusName: "Total NDR",
      statusNumber: dashboardData?.ndr?.totalNdr || 0,
    },
    {
      statusName: "Action Requires",
      statusNumber: dashboardData?.ndr?.actionRequires || 0,
    },
    {
      statusName: "Action Requested",
      statusNumber: dashboardData?.ndr?.actionRequested || 0,
    },
    {
      statusName: "Delivered",
      statusNumber: dashboardData?.ndr?.delivered || 0,
    },
    {
      statusName: "RTO",
      statusNumber: dashboardData?.ndr?.rto || 0,
    },
  ];

  const StatsCod = [
    {
      statusName: "Total Remitted",
      statusNumber: dashboardData?.cod?.totalRemittance || 0,
    },
    {
      statusName: "Last Remittance",
      statusNumber: dashboardData?.cod?.lastRemittance || 0,
    },
    {
      statusName: "Next Remittance",
      statusNumber: (dashboardData?.cod?.totalDue * 0.3).toFixed(2),
    },
    {
      statusName: "Total Remitted Due",
      statusNumber: dashboardData?.cod?.totalDue || 0,
    },
  ];

  // Chart Data
  const chartData = [
    {
      Zone: "Zone 1",
      Shipments: dashboardData?.zoneDistribution?.zones?.zone1,
    },
    {
      Zone: "Zone 2",
      Shipments: dashboardData?.zoneDistribution?.zones?.zone2,
    },
    {
      Zone: "Zone 3",
      Shipments: dashboardData?.zoneDistribution?.zones?.zone3,
    },
    {
      Zone: "Zone 4",
      Shipments: dashboardData?.zoneDistribution?.zones?.zone4,
    },
    {
      Zone: "Zone 5",
      Shipments: dashboardData?.zoneDistribution?.zones?.zone5,
    },
    {
      Zone: "Zone 6",
      Shipments: dashboardData?.zoneDistribution?.zones?.zone6,
    },
  ];

  // Slider Data
  const slides = banners.map((banner) => {
    return {
      image: banner?.imageUrl,
      title: banner?.title,
      subtitle: banner?.subtitle,
      bg: banner.bg ? `bg-[${banner.bg}]` : "",
    };
  });

  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Rank",
        accessorKey: "rank",
        meta: {
          tooltip: (row) => row.rank,
        },
        width: "50px",
      }),
      ColumnHelper({
        header: "Courier Name",
        accessorKey: "name",
        meta: {
          tooltip: (row) => row.name,
        },
      }),
      ColumnHelper({
        header: "Total Shipment",
        accessorKey: "totalShipments",
        meta: {
          tooltip: (row) => row.totalShipments,
        },
      }),
      ColumnHelper({
        header: "Pending",
        accessorKey: "pending",
        meta: {
          tooltip: (row) => row.pending,
        },
      }),
      ColumnHelper({
        header: "In - Transit",
        accessorKey: "inTransit",
        meta: {
          tooltip: (row) => row.inTransit,
        },
      }),
      ColumnHelper({
        header: "Delivered",
        accessorKey: "delivered",
        meta: {
          tooltip: (row) => row.delivered,
        },
      }),
      ColumnHelper({
        header: "RTO",
        accessorKey: "rto",
        meta: {
          tooltip: (row) => row.rto,
        },
      }),
      ColumnHelper({
        header: "Lost/Damage",
        accessorKey: "lostDamaged",
        meta: {
          tooltip: (row) => row.lostDamaged,
        },
      }),
    ],
    []
  );

  const dateRange = useSelector((state) => dashboardData?.dateRange) || {};

  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;

  const [date, setDate] = useState({
    from: startOfMonthDate,
    to: endOfMonthDate,
  });

  const handleDateChange = useCallback(
    (dateRange) => {
      setDate({
        from: dateRange?.from || "",
        to: dateRange?.to || "",
      });
    },
    [startOfMonthDate, endOfMonthDate]
  );

  const dispatch = useDispatch();
  const isDashboardDataLoading = useSelector(
    (state) => state.dashboard.loading
  );
  const prevDateRef = useRef(date);

  const fetchData = async () => {
    if (!date.from || !date.to) return;
    if (isDashboardDataLoading) return;

    // Check if date range has actually changed
    if (
      prevDateRef.current.from?.getTime() === date.from?.getTime() &&
      prevDateRef.current.to?.getTime() === date.to?.getTime() &&
      dashboardData
    ) {
      return;
    }

    const response = await dispatch(
      fetchDashboardData({
        startDate: date.from,
        endDate: date.to,
      })
    );

    if (fetchDashboardData.fulfilled.match(response)) {
      console.log(
        "[Date] Dashboard Data fetched successfully :-",
        response.payload
      );
    } else if (fetchDashboardData.rejected.match(response)) {
      console.log("Data fetch failed :- ", response.payload);
    }

    // Update the previous date reference
    prevDateRef.current = date;
  };

  useEffect(() => {
    fetchData();
  }, [date, dispatch]);

  const handleSearch = (type, value) => {
    navigate(`/tracking/?${type}=${value}`);
  };

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Carousel */}
        {banners.length > 0 && (
          <div>
            <CarouselImageSlider autoSlide={true} slides={slides} />
          </div>
        )}

        {/* KYC Info Dialog */}
        {!user?.kycStatus || user?.kycStatus === "not_submitted" ? (
          <div className="bg-gradient-to-r from-[#3751FE]/95 to-[#2D4CFE] rounded-2xl overflow-hidden shadow-lg">
            <div className="flex flex-col md:flex-row items-center p-4 md:p-6">
              {/* Left side - Icon and illustration */}
              <div className="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
                <div className="bg-white/20 p-4 rounded-full w-24 h-24 flex items-center justify-center mx-auto md:mx-0">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
              </div>

              {/* Right side - Content */}
              <div className="flex-1 text-white text-center md:text-left">
                <h2 className="text-xl md:text-2xl font-bold font-nunito mb-2">
                  Complete Your KYC Verification
                </h2>
                <p className="text-white/90 mb-4 lg:max-w-[80%]">
                  To ensure a seamless shipping experience and unlock all
                  features, please complete your KYC verification. This helps us
                  comply with regulations and protect your account.
                </p>
                <div className="flex flex-col md:flex-row gap-3 justify-center md:justify-start">
                  <button
                    onClick={() => navigate("/settings")}
                    className="bg-white text-[#3751FE] hover:bg-white/90 transition-all px-6 py-2 rounded-lg font-medium flex items-center justify-center gap-2"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Complete KYC Now
                  </button>
                  <AlertDialog>
                    <AlertDialogTrigger>
                      <button className="bg-transparent border border-white/30 hover:bg-white/10 transition-all text-white px-6 py-2 rounded-lg font-medium">
                        Learn More
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="w-[90%] md:w-[60%] max-w-[600px] p-6 gap-4 bg-background max-h-[80vh] overflow-y-auto">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-headline4 font-nunito text-secondary/80 font-bold sticky top-0 bg-background py-2">
                          Why KYC is Important?
                        </AlertDialogTitle>
                        <div className="space-y-4 mt-4 overflow-y-auto">
                          <div className="space-y-3">
                            {[
                              {
                                title: "Mandatory for Security & Verification",
                                description:
                                  "KYC helps us verify your identity and protect your account from fraud and misuse.",
                              },
                              {
                                title: "Prevent Fake or Unauthorized Orders",
                                description:
                                  "It ensures only verified users can access shipping services, reducing fake or risky orders.",
                              },
                              {
                                title: "Enable All Portal Features",
                                description:
                                  "Without completing KYC, your access to important features like order creation, tracking, billing, and API integration is restricted.",
                              },
                              {
                                title: "Required by Courier Partners",
                                description:
                                  "Our partnered courier companies require verified sender information for legal and operational reasons.",
                              },
                              {
                                title: "Helps in Faster Claims and Support",
                                description:
                                  "In case of any issue or claim, verified KYC helps us provide faster resolution and better support.",
                              },
                              {
                                title:
                                  "Builds Trust for B2B & eCommerce Transactions",
                                description:
                                  "Verified users gain more trust when sending parcels, especially in COD and high-value shipments.",
                              },
                              {
                                title: "Regulatory Compliance",
                                description:
                                  "It is mandatory as per Indian courier and logistics compliance standards to avoid penalties or account blocks.",
                              },
                            ].map((item, index) => (
                              <div key={index} className="flex gap-3">
                                <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-text-secondary/70" />
                                <div>
                                  <h3 className="font-semibold text-secondary/90">
                                    {item.title}
                                  </h3>
                                  <p className="text-secondary/70">
                                    {item.description}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </AlertDialogHeader>
                      <AlertDialogFooter className="sticky bottom-0 bg-background py-2">
                        <AlertDialogCancel className="bg-secondary/10 hover:bg-secondary/20 rounded-xl text-secondary/90">
                          Close
                        </AlertDialogCancel>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="hidden md:block absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mt-10 -mr-10"></div>
              <div className="hidden md:block absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -mb-8 -ml-8"></div>
            </div>
          </div>
        ) : null}

        {/* Filters & Wallet */}
        <div className="flex flex-col md:flex-row gap-2 w-full md:gap-4 justify-between">
          <div className="flex flex-col md:flex-row gap-2 md:gap-4">
            <DateRangePicker value={date} onChange={handleDateChange} />
            <div className="flex gap-2  bg-background_2/90 rounded-2xl p-3 items-center w-full md:w-fit">
              <IoWallet className="text-secondary/95 text-subtitle0 md:text-subtitle1" />
              <div className="flex gap-[1px] items-center">
                <FaIndianRupeeSign className="text-secondary/95 text-subtitle2" />
                <p className="md:text-subtitle1 text-body1 font-nunito text-secondary/95 font-semibold pr-1.5">
                  {walletBalance.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }) || 0}
                </p>
              </div>
            </div>
          </div>
          <SearchBar onSearch={handleSearch} />
        </div>

        {/* Stats & Status */}
        <StatsMatrix cards={DeliveryInfoData} />
        <StatsContainer status={StatsShipment} label="Shipment Details" />
        <StatsContainer status={StatsNdr} label="NDR Details" />
        <StatsContainer status={StatsCod} label="COD Status" lgCols={4} />

        {/* Charts & Notifications */}
        <div className="flex flex-col md:flex-row gap-2 w-full md:gap-4">
          <ChartSection chartData={chartData} />
          <div className="bg-background_2/90 rounded-xl p-3 flex flex-col gap-2 w-full md:w-1/2">
            <h2 className="md:text-headline4 text-subtitle1 font-nunito font-bold text-secondary/95">
              Recent Notifications
            </h2>
            <div className="flex flex-col gap-4">
              {notificationLoading ? (
                <>
                  <NotificationSkeleton />
                  <NotificationSkeleton />
                </>
              ) : notifications.length > 0 ? (
                notifications.map((notification) => (
                  <NotificationCard
                    key={notification.id}
                    image={notification.image}
                    title={notification.title}
                    date={notification.date}
                    description={notification.description}
                  />
                ))
              ) : (
                <p className="text-center text-gray-500 mt-4">
                  No notifications available
                </p>
              )}
            </div>
          </div>
        </div>
        <DataTable
          columns={columns}
          tittle="Courier Performance"
          data={dashboardData?.courierPerformance || []}
          showCheckbox={false}
          actions={[
            {
              name: "Export",
              onClick: () => { },
              icon: RiFileExcel2Line,
              body: {
                model: "Courier Performance",
                filename: `Courier_Performance${new Date().toISOString().split("T")[0]
                  }`,
                date: date,
              },
            },
          ]}
        />
      </div>
    </SpaceWrapper>
  );
};

export default DashBoard;
